import { Link } from 'react-router-dom';
import PageHeader from '../components/PageHeader';
import StatusBadge from '../components/StatusBadge';
import { useApi } from '../lib/api';
import { formatCents, formatDate, formatRelative } from '../lib/format';

type Payment = {
  id: string;
  experience_id: string;
  lemon_order_id: string;
  lemon_variant_id: string;
  status: string;
  amount_cents: number;
  currency: string;
  created_at: string;
  recipient_name: string | null;
  organizer_email: string | null;
};

type FailedUpload = {
  id: string;
  experience_id: string;
  guest_id: string | null;
  media_type: string;
  status: string;
  file_size_bytes: string | null;
  error_message: string | null;
  created_at: string;
  updated_at: string;
  recipient_name: string | null;
  guest_name: string | null;
};

type Resp = { recentPayments: Payment[]; failedUploads: FailedUpload[] };

export default function Webhooks() {
  const { data, error, loading } = useApi<Resp>('/api/webhooks');

  return (
    <div>
      <PageHeader
        title="Webhooks & Errors"
        subtitle="Recent Lemon Squeezy payments and failed uploads (derived; no failed_webhooks table yet)"
      />

      {loading && <p className="text-sm text-slate-500">Loading…</p>}
      {error && <p className="text-sm text-red-600">Error: {error.message}</p>}

      {data && (
        <div className="space-y-8">
          <section>
            <h2 className="mb-3 text-sm font-semibold text-slate-700">Recent payments (last 50)</h2>
            <div className="overflow-x-auto rounded-lg border border-slate-200 bg-white">
              <table className="min-w-full text-sm">
                <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
                  <tr>
                    <th className="px-4 py-2 font-medium">Order</th>
                    <th className="px-4 py-2 font-medium">Status</th>
                    <th className="px-4 py-2 font-medium">Experience</th>
                    <th className="px-4 py-2 font-medium">Organizer</th>
                    <th className="px-4 py-2 font-medium text-right">Amount</th>
                    <th className="px-4 py-2 font-medium">When</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {data.recentPayments.map((p) => (
                    <tr key={p.id}>
                      <td className="px-4 py-2 font-mono text-xs">{p.lemon_order_id}</td>
                      <td className="px-4 py-2"><StatusBadge status={p.status} /></td>
                      <td className="px-4 py-2">
                        {p.experience_id ? (
                          <Link to={`/experiences/${p.experience_id}`} className="text-blue-700 hover:underline">
                            {p.recipient_name ?? '—'}
                          </Link>
                        ) : '—'}
                      </td>
                      <td className="px-4 py-2 text-slate-700">{p.organizer_email ?? '—'}</td>
                      <td className="px-4 py-2 text-right tabular-nums">{formatCents(p.amount_cents, p.currency)}</td>
                      <td className="px-4 py-2 text-slate-600" title={p.created_at}>{formatRelative(p.created_at)}</td>
                    </tr>
                  ))}
                  {data.recentPayments.length === 0 && (
                    <tr><td colSpan={6} className="px-4 py-6 text-center text-slate-500">No payments yet.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </section>

          <section>
            <h2 className="mb-3 text-sm font-semibold text-slate-700">Failed uploads (last 100)</h2>
            <div className="overflow-x-auto rounded-lg border border-slate-200 bg-white">
              <table className="min-w-full text-sm">
                <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
                  <tr>
                    <th className="px-4 py-2 font-medium">Experience</th>
                    <th className="px-4 py-2 font-medium">Guest</th>
                    <th className="px-4 py-2 font-medium">Type</th>
                    <th className="px-4 py-2 font-medium">Error</th>
                    <th className="px-4 py-2 font-medium">Last update</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {data.failedUploads.map((u) => (
                    <tr key={u.id}>
                      <td className="px-4 py-2">
                        <Link to={`/experiences/${u.experience_id}`} className="text-blue-700 hover:underline">
                          {u.recipient_name ?? '—'}
                        </Link>
                      </td>
                      <td className="px-4 py-2">{u.guest_name ?? <span className="italic text-slate-500">organizer</span>}</td>
                      <td className="px-4 py-2">{u.media_type}</td>
                      <td className="px-4 py-2 text-red-600 text-xs max-w-md truncate" title={u.error_message ?? ''}>
                        {u.error_message ?? '—'}
                      </td>
                      <td className="px-4 py-2 text-slate-600" title={formatDate(u.updated_at)}>{formatRelative(u.updated_at)}</td>
                    </tr>
                  ))}
                  {data.failedUploads.length === 0 && (
                    <tr><td colSpan={5} className="px-4 py-6 text-center text-slate-500">No failed uploads.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </section>
        </div>
      )}
    </div>
  );
}
